import { useState, useEffect, useCallback, memo } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Bus, Plus, Wifi, Wind, Usb, Bath, Edit, Trash2, Loader2, History, Activity
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEAT_LAYOUTS = [
  { value: '2-2', label: '2-2 (Standard Coach)', description: '2 seats each side of aisle' },
  { value: '2-3', label: '2-3 (High Capacity)', description: '2 seats one side, 3 seats other side' },
  { value: '2-1', label: '2-1 (Luxury)', description: '2 seats one side, 1 seat other side' },
];

const amenityOptions = ['WiFi', 'AC', 'USB Charging', 'Toilet'];

// Memoized form component to prevent re-renders causing input lag
const BusFormFields = memo(({ formData, onChange, onAmenityToggle }) => {
  const handleChange = useCallback((field, value) => {
    onChange(field, value);
  }, [onChange]);

  return (
    <div className="space-y-4">
      <div>
        <Label>Registration Number *</Label>
        <Input
          defaultValue={formData.registration_number}
          onBlur={(e) => handleChange('registration_number', e.target.value)}
          placeholder="e.g., AEA 1234"
          data-testid="bus-registration-input"
        />
      </div>

      <div>
        <Label>Model *</Label>
        <Input
          defaultValue={formData.model}
          onBlur={(e) => handleChange('model', e.target.value)}
          placeholder="e.g., Scania Touring"
          data-testid="bus-model-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Seat Capacity *</Label>
          <Input
            type="number"
            defaultValue={formData.capacity}
            onBlur={(e) => handleChange('capacity', Number(e.target.value))}
            placeholder="e.g., 55, 65, 74"
            data-testid="bus-capacity-input"
          />
        </div>
        <div>
          <Label>Seat Layout *</Label>
          <Select 
            defaultValue={formData.seat_layout} 
            onValueChange={(v) => handleChange('seat_layout', v)}
          >
            <SelectTrigger data-testid="bus-layout-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEAT_LAYOUTS.map(layout => (
                <SelectItem key={layout.value} value={layout.value}>
                  <div>
                    <span className="font-medium">{layout.label}</span>
                    <span className="text-xs text-slate-500 ml-2">{layout.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Seat Layout Preview */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <Label className="text-xs text-slate-500">Seat Layout Preview</Label>
        <div className="flex items-center justify-center gap-4 mt-2">
          {formData.seat_layout === '2-2' && (
            <div className="flex gap-1">
              <div className="flex flex-col gap-1">
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
              </div>
              <div className="w-4 bg-slate-200 rounded" />
              <div className="flex flex-col gap-1">
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
              </div>
            </div>
          )}
          {formData.seat_layout === '2-3' && (
            <div className="flex gap-1">
              <div className="flex flex-col gap-1">
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
              </div>
              <div className="w-4 bg-slate-200 rounded" />
              <div className="flex flex-col gap-1">
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
              </div>
            </div>
          )}
          {formData.seat_layout === '2-1' && (
            <div className="flex gap-1">
              <div className="flex flex-col gap-1">
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
                <div className="w-6 h-6 bg-panashe-red/20 rounded" />
              </div>
              <div className="w-4 bg-slate-200 rounded" />
              <div className="flex flex-col gap-1">
                <div className="w-8 h-8 bg-panashe-gold/30 rounded" />
              </div>
            </div>
          )}
          <span className="text-sm text-slate-500 ml-4">
            {formData.seat_layout} layout × {Math.ceil(formData.capacity / (formData.seat_layout === '2-2' ? 4 : formData.seat_layout === '2-3' ? 5 : 3))} rows ≈ {formData.capacity} seats
          </span>
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Amenities</Label>
        <div className="grid grid-cols-2 gap-2">
          {amenityOptions.map(amenity => (
            <div key={amenity} className="flex items-center gap-2">
              <Checkbox
                checked={formData.amenities.includes(amenity)}
                onCheckedChange={() => onAmenityToggle(amenity)}
                data-testid={`amenity-${amenity.toLowerCase().replace(' ', '-')}`}
              />
              <Label className="cursor-pointer">{amenity}</Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

BusFormFields.displayName = 'BusFormFields';

const AdminBuses = () => {
  const { token } = useAuth();
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBus, setEditingBus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fleetBus, setFleetBus] = useState(null);
  const [fleetHistory, setFleetHistory] = useState(null);
  const [busPerformance, setBusPerformance] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [recordSaving, setRecordSaving] = useState(false);
  const [fleetRecord, setFleetRecord] = useState({
    category: 'mileage', description: '', odometer_km: '', document_number: '', expiry_date: '', vendor: ''
  });
  
  const [formData, setFormData] = useState({
    registration_number: '',
    model: '',
    capacity: 50,
    seat_layout: '2-2',
    amenities: []
  });

  useEffect(() => {
    fetchBuses();
  }, []);

  const fetchBuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/buses`);
      setBuses(response.data);
    } catch (error) {
      toast.error('Failed to load buses');
    } finally {
      setLoading(false);
    }
  };

  const openFleetHistory = async (bus) => {
    setFleetBus(bus);
    setFleetHistory(null);
    setBusPerformance(null);
    setFleetLoading(true);
    try {
      const [historyResponse, performanceResponse] = await Promise.all([
        axios.get(`${API_URL}/buses/${bus.id}/history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/buses/${bus.id}/performance`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFleetHistory(historyResponse.data);
      setBusPerformance(performanceResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load bus history');
    } finally {
      setFleetLoading(false);
    }
  };

  const handleFleetRecord = async (event) => {
    event.preventDefault();
    if (!fleetBus) return;
    setRecordSaving(true);
    try {
      await axios.post(`${API_URL}/buses/${fleetBus.id}/records`, {
        ...fleetRecord,
        odometer_km: fleetRecord.odometer_km === '' ? null : Number(fleetRecord.odometer_km),
        expiry_date: fleetRecord.expiry_date || null,
        document_number: fleetRecord.document_number || null,
        vendor: fleetRecord.vendor || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bus record saved');
      setFleetRecord({ category: 'mileage', description: '', odometer_km: '', document_number: '', expiry_date: '', vendor: '' });
      await openFleetHistory(fleetBus);
      fetchBuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bus record');
    } finally {
      setRecordSaving(false);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleAmenityToggle = useCallback((amenity) => {
    setFormData(prev => {
      const newAmenities = prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity];
      return { ...prev, amenities: newAmenities };
    });
  }, []);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/buses`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bus added successfully!');
      setDialogOpen(false);
      resetForm();
      fetchBuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bus');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/buses/${editingBus.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bus updated!');
      setEditingBus(null);
      resetForm();
      fetchBuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bus');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (busId) => {
    if (!window.confirm('Are you sure you want to delete this bus?')) return;
    try {
      await axios.delete(`${API_URL}/buses/${busId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bus deleted');
      fetchBuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bus');
    }
  };

  const startEdit = (bus) => {
    setEditingBus(bus);
    setFormData({
      registration_number: bus.registration_number,
      model: bus.model,
      capacity: bus.capacity,
      seat_layout: bus.seat_layout || '2-2',
      amenities: bus.amenities || []
    });
  };

  const resetForm = () => {
    setFormData({
      registration_number: '',
      model: '',
      capacity: 50,
      seat_layout: '2-2',
      amenities: []
    });
  };

  const getAmenityIcon = (amenity) => {
    const icons = { 'WiFi': Wifi, 'AC': Wind, 'USB Charging': Usb, 'Toilet': Bath };
    return icons[amenity] || Wifi;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-panashe-red" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-buses-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Manage Buses</h1>
            <p className="text-slate-600">{buses.length} buses in fleet</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-panashe" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-bus-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Bus
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Bus</DialogTitle>
              </DialogHeader>
              <BusFormFields 
                formData={formData} 
                onChange={handleFieldChange}
                onAmenityToggle={handleAmenityToggle}
              />
              <Button className="w-full btn-panashe mt-4" onClick={handleCreate} disabled={saving} data-testid="submit-bus-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Add Bus
              </Button>
            </DialogContent>
          </Dialog>
        </div>

        {/* Buses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {buses.map(bus => (
            <Card key={bus.id} className="hover:shadow-md transition-shadow" data-testid={`bus-card-${bus.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-panashe-red/10 rounded-lg flex items-center justify-center">
                    <Bus className="h-6 w-6 text-panashe-red" />
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openFleetHistory(bus)} title="Bus performance, documents, and history" data-testid={`bus-history-${bus.id}`}>
                      <Activity className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(bus)} data-testid={`edit-bus-${bus.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(bus.id)} data-testid={`delete-bus-${bus.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <h3 className="font-heading text-xl font-bold mb-1">{bus.registration_number}</h3>
                <p className="text-slate-600 mb-4">{bus.model}</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Capacity</span>
                    <span className="font-medium">{bus.capacity} seats</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Layout</span>
                    <Badge variant="outline">{bus.seat_layout || '2-2'}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <Badge className={bus.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100'}>
                      {bus.status}
                    </Badge>
                  </div>
                </div>
                
                {bus.amenities?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                    {bus.amenities.map((amenity, idx) => {
                      const Icon = getAmenityIcon(amenity);
                      return (
                        <Badge key={idx} variant="outline" className="text-xs">
                          <Icon className="h-3 w-3 mr-1" />
                          {amenity}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {buses.length === 0 && (
          <Card className="p-12 text-center">
            <Bus className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Buses Added</h3>
            <p className="text-slate-600 mb-4">Start by adding your first bus to the fleet</p>
            <Button className="btn-panashe" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Bus
            </Button>
          </Card>
        )}

        <Dialog open={!!fleetBus} onOpenChange={(open) => { if (!open) setFleetBus(null); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> {fleetBus?.registration_number} Fleet History</DialogTitle>
            </DialogHeader>
            {fleetLoading && <div className="py-8 text-center text-slate-500">Loading fleet records...</div>}
            {busPerformance && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Trips / Completed</p><p className="text-xl font-bold">{busPerformance.trip_count} / {busPerformance.completed_trips}</p></CardContent></Card>
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Passengers</p><p className="text-xl font-bold">{busPerformance.passenger_count}</p><p className="text-xs text-slate-500">{busPerformance.seat_utilization_percent}% seat utilization</p></CardContent></Card>
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Revenue USD equivalent</p><p className="text-xl font-bold">${Number(busPerformance.revenue_usd_equivalent || 0).toFixed(2)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Approved costs / estimated profit</p><p className="text-sm font-bold">${Number(busPerformance.expenses_usd_equivalent || 0).toFixed(2)} / ${Number(busPerformance.operating_profit_usd_estimate || 0).toFixed(2)}</p></CardContent></Card>
              </div>
            )}
            {busPerformance?.documents?.length > 0 && (
              <div className="border rounded-md p-4">
                <h3 className="font-semibold mb-2">Document Expiry</h3>
                <div className="space-y-2">
                  {busPerformance.documents.map(record => {
                    const expired = new Date(record.expiry_date).getTime() < Date.now();
                    const dueSoon = !expired && new Date(record.expiry_date).getTime() < Date.now() + 30 * 86400000;
                    return (
                      <div key={record.id} className="flex flex-wrap justify-between gap-2 text-sm">
                        <span className="capitalize">{record.category} · {record.document_number || record.description}</span>
                        <span className={expired ? 'text-red-700 font-semibold' : dueSoon ? 'text-amber-700 font-semibold' : 'text-slate-600'}>{expired ? 'Expired' : dueSoon ? 'Due soon' : 'Expires'} {record.expiry_date}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <form onSubmit={handleFleetRecord} className="border rounded-md p-4 space-y-3">
              <h3 className="font-semibold">Add Mileage or Document Record</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Record type</Label>
                  <select className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" value={fleetRecord.category} onChange={event => setFleetRecord({ ...fleetRecord, category: event.target.value })}>
                    <option value="mileage">Odometer / Mileage</option>
                    <option value="insurance">Insurance</option>
                    <option value="licensing">Licensing</option>
                    <option value="inspection">Inspection</option>
                    <option value="registration">Registration</option>
                    <option value="maintenance">Maintenance note</option>
                    <option value="repairs">Repair note</option>
                  </select>
                </div>
                {fleetRecord.category === 'mileage' ? (
                  <div><Label>Odometer km</Label><Input type="number" min="0" step="0.1" required value={fleetRecord.odometer_km} onChange={event => setFleetRecord({ ...fleetRecord, odometer_km: event.target.value })} /></div>
                ) : (
                  <div><Label>Expiry date</Label><Input type="date" required={['insurance', 'licensing', 'inspection', 'registration'].includes(fleetRecord.category)} value={fleetRecord.expiry_date} onChange={event => setFleetRecord({ ...fleetRecord, expiry_date: event.target.value })} /></div>
                )}
                <div><Label>Record / document number</Label><Input value={fleetRecord.document_number} onChange={event => setFleetRecord({ ...fleetRecord, document_number: event.target.value })} /></div>
                <div><Label>Vendor / authority</Label><Input value={fleetRecord.vendor} onChange={event => setFleetRecord({ ...fleetRecord, vendor: event.target.value })} /></div>
                <div className="md:col-span-2"><Label>Description</Label><Input required value={fleetRecord.description} onChange={event => setFleetRecord({ ...fleetRecord, description: event.target.value })} placeholder="Service notes, policy details, inspection result" /></div>
              </div>
              <Button type="submit" disabled={recordSaving}>{recordSaving ? 'Saving...' : 'Save Bus Record'}</Button>
            </form>
            {fleetHistory && (
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="border rounded-md p-4">
                  <h3 className="font-semibold mb-2">Bus Records</h3>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {fleetHistory.records.map(record => <div key={record.id} className="border-b pb-2 text-sm"><p className="font-medium capitalize">{record.category}: {record.description}</p><p className="text-xs text-slate-500">{record.recorded_at?.slice(0, 10)} · {record.recorded_by_name}{record.odometer_km != null ? ` · ${record.odometer_km} km` : ''}</p></div>)}
                    {!fleetHistory.records.length && <p className="text-sm text-slate-500">No records yet.</p>}
                  </div>
                </div>
                <div className="border rounded-md p-4">
                  <h3 className="font-semibold mb-2">Expense History</h3>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {fleetHistory.expenses.map(expense => <div key={expense.id} className="border-b pb-2 text-sm"><p className="font-medium capitalize">{expense.category} · {expense.currency} {Number(expense.amount || 0).toFixed(2)}</p><p className="text-xs text-slate-500">{expense.description} · {expense.status} · {expense.created_at?.slice(0, 10)}</p></div>)}
                    {!fleetHistory.expenses.length && <p className="text-sm text-slate-500">No expenses recorded.</p>}
                  </div>
                </div>
                <div className="border rounded-md p-4">
                  <h3 className="font-semibold mb-2">Trip History</h3>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {fleetHistory.trips.map(trip => <div key={trip.id} className="border-b pb-2 text-sm"><p className="font-medium">{trip.trip_number || trip.id}</p><p className="text-xs text-slate-500">{trip.departure_datetime?.slice(0, 10)} · {trip.status}</p></div>)}
                    {!fleetHistory.trips.length && <p className="text-sm text-slate-500">No trips recorded.</p>}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={!!editingBus} onOpenChange={() => setEditingBus(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Bus</DialogTitle>
            </DialogHeader>
            <BusFormFields 
              formData={formData} 
              onChange={handleFieldChange}
              onAmenityToggle={handleAmenityToggle}
            />
            <Button className="w-full btn-panashe mt-4" onClick={handleEdit} disabled={saving} data-testid="update-bus-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Update Bus
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminBuses;
